import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { UserCircle, Bell, LogOut, X } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useUser } from '../../contexts/UserContext';
import { ROLE_CONFIG } from '../../constants/roles';
import { cn } from '../../utils/cn';

interface LoginButtonProps {
  variant?: 'light' | 'dark' | 'transparent';
}

export function LoginButton({ variant = 'light' }: LoginButtonProps) {
  const { isAuthenticated, user, logout } = useAuth();
  const { currentResidence } = useUser();
  const navigate = useNavigate();
  const [showDialog, setShowDialog] = useState(false);

  const handleButtonClick = () => setShowDialog(true);

  const getIconColor = () => {
    switch (variant) {
      case 'light':
        return 'text-condo-dark';
      case 'dark':
        return 'text-condo-light';
      case 'transparent':
      default:
        return 'text-condo-light';
    }
  };

  const roleLabel = (() => {
    const role = currentResidence?.role || null;
    if (!role) return 'Beboer';
    const cfg = ROLE_CONFIG[role as keyof typeof ROLE_CONFIG];
    return cfg?.label || role;
  })();

  return (
    <>
      <button
        onClick={handleButtonClick}
        className="p-2 rounded-full transition-all"
        aria-label="Open user menu"
      >
        <UserCircle className={cn('sm:h-6 sm:w-6 h-7 w-7', getIconColor())} />
      </button>

      {showDialog && createPortal(
        <div
          className="fixed inset-0 bg-black/20 backdrop-blur-sm flex items-center justify-center p-4 z-[9999]"
          onClick={() => setShowDialog(false)}
          role="dialog"
          aria-modal="true"
        >
          <div
            className="bg-white rounded-xl shadow-xl max-w-md w-full max-h-[90vh] overflow-auto pointer-events-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="relative sm:p-6 p-4">
              <button
                onClick={() => setShowDialog(false)}
                className="absolute right-3 top-3 p-2 hover:bg-gray-100 rounded-lg"
                aria-label="Lukk"
              >
                <X className="h-5 w-5 text-gray-500" />
              </button>

              <h3 className="text-lg font-semibold mb-4">Profil</h3>

              {isAuthenticated ? (
                <div className="space-y-4">
                  {/* Fullt navn */}
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">Fullt navn</span>
                    <span className="font-medium text-gray-900">{user?.name || 'Ukjent'}</span>
                  </div>

                  {/* Rolle(r) */}
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">Rolle(r)</span>
                    <span className="font-medium text-gray-900">{roleLabel}</span>
                  </div>

                  {/* E-post (valgfritt å vise under navnet om ønskelig) */}
                  {user?.email && (
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-600">E‑post</span>
                      <span className="font-medium text-gray-900">{user.email}</span>
                    </div>
                  )}

                  <div className="pt-2 flex flex-col gap-2">
                    <button
                      type="button"
                      onClick={() => {
                        setShowDialog(false);
                        navigate('/profil');
                      }}
                      className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 rounded-md border hover:bg-gray-50"
                    >
                      <Bell className="h-4 w-4" />
                      Rediger varslingsinnstillinger
                    </button>

                    <button
                      type="button"
                      onClick={async () => {
                        await logout();
                        setShowDialog(false);
                      }}
                      className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 rounded-md border text-red-600 hover:bg-red-50"
                    >
                      <LogOut className="h-4 w-4" />
                      Logg ut
                    </button>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <p className="text-sm text-gray-600">Vennligst logg inn fra innloggingssiden.</p>
                  <a
                    href="/login"
                    className="inline-flex items-center justify-center px-4 py-2 rounded-md border hover:bg-gray-50"
                    onClick={() => setShowDialog(false)}
                  >
                    Gå til innlogging
                  </a>
                </div>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}